/**
 * RegisterForm Component
 * Handles user registration with validation
 * 
 * Requirements: 1.1, 1.2, 1.3, 1.4
 */

import { useState } from 'react';
import { User, Mail, Lock, Eye, EyeOff, Phone, CheckCircle, Users, GraduationCap } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const RegisterForm = ({ onSuccess, initialRole = 'parent' }) => {
  const { register } = useAuth();

  // Form state — role pré-sélectionné selon le CTA cliqué sur la landing
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
    role: initialRole
  });
  
  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Validate email format
   * Requirement: 1.2 - Email validation
   */
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Validate password length
   * Requirement: 1.3 - Password length validation
   */
  const validatePassword = (password) => {
    return password.length >= 8;
  };

  /**
   * Validate password match
   * Requirement: 1.4 - Password confirmation match
   */
  const validatePasswordMatch = (password, confirmation) => {
    return password === confirmation;
  };

  /**
   * Validate entire form
   * Returns validation errors object
   */
  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'Le prénom est requis';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Le nom est requis';
    }
    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "L'email n'est pas valide";
    }
    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (!validatePassword(formData.password)) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }
    if (!formData.password_confirmation) {
      newErrors.password_confirmation = 'La confirmation du mot de passe est requise';
    } else if (!validatePasswordMatch(formData.password, formData.password_confirmation)) {
      newErrors.password_confirmation = 'Les mots de passe ne correspondent pas';
    }

    return newErrors;
  };

  /**
   * Handle input change
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  /**
   * Handle role selection
   */
  const handleRoleChange = (selectedRole) => {
    setFormData(prev => ({
      ...prev,
      role: selectedRole
    }));
  };

  /**
   * Handle form submission
   * Requirement: 1.1 - User registration
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Call register method from AuthContext
      await register(formData);
      
      // Requirement: 1.5 - Redirect to dashboard after successful registration
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Handle API errors
      if (error.response?.data?.errors) {
        // Requirement: 1.2 - Display validation error for duplicate email
        setErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: "Une erreur s'est produite lors de l'inscription" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Role Selection - Requirement: 1.1 */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <button 
          type="button"
          onClick={() => handleRoleChange('parent')}
          className={`
            relative p-5 rounded-2xl border-2 transition-all duration-300 group
            ${formData.role === 'parent' 
              ? 'border-[#17386E] bg-[#17386E] shadow-lg scale-105' 
              : 'border-gray-200 bg-white hover:border-[#4CE0C2] hover:shadow-md'
            }
          `}
        >
          <div className="flex flex-col items-center gap-2">
            <div className={`
              p-3 rounded-xl transition-colors
              ${formData.role === 'parent' 
                ? 'bg-white/20 text-white' 
                : 'bg-[#EAF4FB] text-[#17386E] group-hover:bg-[#4CE0C2]/20'
              }
            `}>
              <Users size={28} strokeWidth={2.5} />
            </div>
            <span className={`
              font-montserrat font-semibold text-sm
              ${formData.role === 'parent' ? 'text-white' : 'text-[#17386E]'}
            `}>
              Je suis Parent
            </span>
          </div>
          {formData.role === 'parent' && (
            <div className="absolute top-2 right-2 w-6 h-6 bg-[#4CE0C2] rounded-full flex items-center justify-center">
              <CheckCircle size={16} className="text-white" />
            </div>
          )}
        </button>

        <button 
          type="button"
          onClick={() => handleRoleChange('tutor')}
          className={`
            relative p-5 rounded-2xl border-2 transition-all duration-300 group
            ${formData.role === 'tutor' 
              ? 'border-[#17386E] bg-[#17386E] shadow-lg scale-105' 
              : 'border-gray-200 bg-white hover:border-[#4CE0C2] hover:shadow-md'
            }
          `}
        >
          <div className="flex flex-col items-center gap-2">
            <div className={`
              p-3 rounded-xl transition-colors
              ${formData.role === 'tutor' 
                ? 'bg-white/20 text-white' 
                : 'bg-[#EAF4FB] text-[#17386E] group-hover:bg-[#4CE0C2]/20'
              }
            `}>
              <GraduationCap size={28} strokeWidth={2.5} />
            </div>
            <span className={`
              font-montserrat font-semibold text-sm
              ${formData.role === 'tutor' ? 'text-white' : 'text-[#17386E]'}
            `}>
              Je suis Répétiteur
            </span>
          </div>
          {formData.role === 'tutor' && (
            <div className="absolute top-2 right-2 w-6 h-6 bg-[#4CE0C2] rounded-full flex items-center justify-center">
              <CheckCircle size={16} className="text-white" />
            </div>
          )}
        </button>
      </div>

      {/* General Error */}
      {errors.general && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          {errors.general}
        </div>
      )}

      {/* Name Fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="relative group">
            <div className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-[#17386E] transition-colors">
              <User size={18}/>
            </div>
            <input 
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="Prénom"
              className={`w-full bg-[#F5F5F5] border rounded-xl py-3 pl-12 pr-4 font-montserrat text-sm text-[#4A4A4A] placeholder-gray-400 focus:bg-white focus:border-[#17386E] focus:outline-none focus:ring-2 focus:ring-[#EAF4FB] transition-all ${
                errors.first_name ? 'border-red-500' : 'border-transparent'
              }`}
            />
          </div>
          {errors.first_name && (
            <p className="mt-1 text-xs text-red-600">{errors.first_name}</p>
          )}
        </div>

        <div>
          <div className="relative group">
            <div className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-[#17386E] transition-colors">
              <User size={18}/>
            </div>
            <input 
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              placeholder="Nom"
              className={`w-full bg-[#F5F5F5] border rounded-xl py-3 pl-12 pr-4 font-montserrat text-sm text-[#4A4A4A] placeholder-gray-400 focus:bg-white focus:border-[#17386E] focus:outline-none focus:ring-2 focus:ring-[#EAF4FB] transition-all ${
                errors.last_name ? 'border-red-500' : 'border-transparent'
              }`}
            />
          </div>
          {errors.last_name && (
            <p className="mt-1 text-xs text-red-600">{errors.last_name}</p>
          )}
        </div>
      </div>

      {/* Email Field */}
      <div>
        <div className="relative group">
          <div className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-[#17386E] transition-colors">
            <Mail size={18}/>
          </div>
          <input 
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Adresse email"
            className={`w-full bg-[#F5F5F5] border rounded-xl py-3 pl-12 pr-4 font-montserrat text-sm text-[#4A4A4A] placeholder-gray-400 focus:bg-white focus:border-[#17386E] focus:outline-none focus:ring-2 focus:ring-[#EAF4FB] transition-all ${
              errors.email ? 'border-red-500' : 'border-transparent'
            }`}
          />
        </div>
        {errors.email && (
          <p className="mt-1 text-xs text-red-600">{errors.email}</p>
        )}
      </div>

      {/* Phone Field */}
      <div>
        <div className="relative group">
          <div className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-[#17386E] transition-colors">
            <Phone size={18}/>
          </div>
          <input 
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Téléphone (optionnel)"
            className="w-full bg-[#F5F5F5] border border-transparent rounded-xl py-3 pl-12 pr-4 font-montserrat text-sm text-[#4A4A4A] placeholder-gray-400 focus:bg-white focus:border-[#17386E] focus:outline-none focus:ring-2 focus:ring-[#EAF4FB] transition-all"
          />
        </div>
      </div>

      {/* Password Field */}
      <div>
        <div className="relative group">
          <div className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-[#17386E] transition-colors">
            <Lock size={18}/>
          </div>
          <input 
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Mot de passe"
            className={`w-full bg-[#F5F5F5] border rounded-xl py-3 pl-12 pr-12 font-montserrat text-sm text-[#4A4A4A] placeholder-gray-400 focus:bg-white focus:border-[#17386E] focus:outline-none focus:ring-2 focus:ring-[#EAF4FB] transition-all ${
              errors.password ? 'border-red-500' : 'border-transparent'
            }`}
          />
          <button 
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-3.5 text-gray-400 hover:text-[#17386E]"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {errors.password && (
          <p className="mt-1 text-xs text-red-600">{errors.password}</p>
        )}
      </div>

      {/* Password Confirmation Field */}
      <div>
        <div className="relative group">
          <div className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-[#17386E] transition-colors">
            <Lock size={18}/>
          </div>
          <input 
            type={showPasswordConfirmation ? "text" : "password"}
            name="password_confirmation"
            value={formData.password_confirmation}
            onChange={handleChange}
            placeholder="Confirmer le mot de passe"
            className={`w-full bg-[#F5F5F5] border rounded-xl py-3 pl-12 pr-12 font-montserrat text-sm text-[#4A4A4A] placeholder-gray-400 focus:bg-white focus:border-[#17386E] focus:outline-none focus:ring-2 focus:ring-[#EAF4FB] transition-all ${
              errors.password_confirmation ? 'border-red-500' : 'border-transparent'
            }`}
          />
          <button 
            type="button"
            onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
            className="absolute right-4 top-3.5 text-gray-400 hover:text-[#17386E]"
          >
            {showPasswordConfirmation ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {errors.password_confirmation && (
          <p className="mt-1 text-xs text-red-600">{errors.password_confirmation}</p>
        )}
      </div>

      {/* Submit Button */}
      <button 
        type="submit"
        disabled={isSubmitting}
        className="btn-primary w-full py-4 rounded-xl font-playfair font-bold text-lg shadow-lg mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'Inscription en cours...' : (
          formData.role === 'parent' ? "Créer mon compte Parent" : "Créer mon compte Répétiteur"
        )}
      </button>
    </form>
  );
};

export default RegisterForm;
